import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Card from '../../../components/common/Card'
import Typography from '../../../components/common/Typography'
import Button from '../../../components/common/Button'
import Miniheader from '../../../components/layout/MiniHeader'
import { useDispatch } from 'react-redux'
import useAppSelector from '../../../state/redux/hooks'
import { getAllActivity } from '../../../state/redux/actions/activityActions'
import LoadingIndicator from '../../../components/state/LoadingIndicator'
import ErrorState from '../../../components/state/ErrorState'
import { formatDateDashed } from '../../../shared/utils/dateUtils'
import Pagination from '../../../components/common/Pagination/Pagination'
import GridTable from '../../../components/common/GridTable/GridTable'

const ActivityList = () => {
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [dateFilter, setDateFilter] = useState(null)
  const [isFiltering, setIsFiltering] = useState(false)

  const { activities, pagination, loading, error } = useAppSelector(state => {
    if (
      state.activity.activities &&
      typeof state.activity.activities === 'object' &&
      !Array.isArray(state.activity.activities) &&
      state.activity.activities.activities &&
      state.activity.activities.pagination
    ) {
      return {
        activities: state.activity.activities.activities,
        pagination: state.activity.activities.pagination,
        loading: state.activity.loading,
        error: state.activity.error,
      }
    }
    return {
      activities: state.activity.activities || [],
      pagination: state.activity.pagination,
      loading: state.activity.loading,
      error: state.activity.error,
    }
  })

  useEffect(() => {
    dispatch(getAllActivity({ page: currentPage }))
  }, [dispatch, currentPage])

  const handleRefresh = () => {
    dispatch(getAllActivity({ page: 1 }))
    setCurrentPage(1)
  }

  // Filter activities by date range
  const filterActivitiesByDate = (activityList, startDate, endDate) => {
    if (!startDate || !endDate || !activityList || !Array.isArray(activityList)) {
      return activityList || []
    }

    return activityList.filter(activity => {
      const activityDate = new Date(activity.createdAt)
      const start = new Date(startDate)
      const end = new Date(endDate)

      // Set time boundaries
      start.setHours(0, 0, 0, 0)
      end.setHours(23, 59, 59, 999)

      return activityDate >= start && activityDate <= end
    })
  }

  // Apply date filter if active, otherwise use all activities
  const filteredActivities = isFiltering && dateFilter
    ? filterActivitiesByDate(activities, dateFilter.startDate, dateFilter.endDate)
    : activities

  // Handle filter change from MiniHeader
  const handleMiniHeaderFilterChange = (filtered, dateRange) => {
    if (!dateRange) {
      // No date filter applied - reset to show all data
      setIsFiltering(false)
      setDateFilter(null)
      return
    }

    // Date filter is applied - set filtering state
    setIsFiltering(true)
    setDateFilter(dateRange)
  }

  const columns = [
    { colname: 'User', colSpan: 'col-span-1', colStyle: 'pl-7' },
    { colname: 'Role', colSpan: 'col-span-1' },
    { colname: 'Action', colSpan: 'col-span-1' },
    { colname: 'Description', colSpan: 'col-span-1' },
    { colname: 'IP Address', colSpan: 'col-span-1' },
    { colname: 'Date', colSpan: 'col-span-1', colStyle: 'pr-7 justify-end' },
  ]

  return (
    <>
      <Miniheader
        title="Activity Logs"
        data={activities || []}
        onFilterChange={handleMiniHeaderFilterChange}
      />

      <div className="ml-[250px] flex-1 px-4 py-8 w-[1040px] mx-auto">
        {loading && <LoadingIndicator />}
        {error && <ErrorState error={error} />}
        {!loading && filteredActivities?.length === 0 && (
          <Card className="text-center p-8 border border-border rounded-md max-w-md w-full mt-[140px]">
            <Typography variant="body" className="text-secondary">
              No activity logs found.
            </Typography>
          </Card>
        )}

        {!loading && !error && filteredActivities?.length > 0 && (
          <>
            <div className="flex justify-between py-4">
              <Card className="mt-[4px] w-48">
                <div className="flex flex-row items-center p-4 space-x-2">
                  <Typography variant="h4">Total Activities <Button>{isFiltering ? filteredActivities?.length : pagination?.totalItems}</Button></Typography>
                  
                </div>

              </Card>

              <Pagination
                currentPage={pagination?.currentPage || 1}
                itemsPerPage={pagination?.itemsPerPage || 20}
                totalItems={pagination?.totalItems || 0}
                onPageChange={setCurrentPage}
                onRefresh={handleRefresh}
                justify="end"
              />
            </div>

            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <GridTable columns={columns} gridCols="grid-cols-6">
                {filteredActivities.map(activity => (
                  <div key={activity._id} className="col-span-6 grid grid-cols-6 gap-4 items-center py-3 border-b border-gray-100 hover:bg-gray-50">
                    {/* User - col-span-1 */}
                    <div className="col-span-1 flex items-center space-x-1 pl-4 justify-start text-left">
                      <div>
                        <p className="text-sm font-medium text-gray-900 text-left">{activity.userName}</p>
                        <p className="text-xs text-gray-500 text-left">{activity.userEmail}</p>
                      </div>
                    </div>
                    
                    {/* Role - col-span-1 */}
                    <div className="col-span-1 text-left">
                      <p className="text-sm text-gray-900 text-left">{activity.userRoleDetail || activity.userRole}</p>
                    </div>
                    
                    {/* Action - col-span-1 */}
                    <div className="col-span-1 text-left">
                      <p className="text-sm text-gray-900 text-left">{activity.action}</p>
                    </div>
                    
                    {/* Description - col-span-1 */}
                    <div className="col-span-1 text-left">
                      <p className="text-sm text-gray-900 truncate text-left">{activity.description}</p>
                    </div>
                    
                    {/* IP Address - col-span-1 */}
                    <div className="col-span-1 text-left">
                      <p className="text-sm text-gray-900 text-left">{activity.ipAddress}</p>
                    </div>
                    
                    {/* Date - col-span-1 */}
                    <div className="col-span-1 pr-4 flex justify-start text-left">
                      <p className="text-sm text-gray-900 text-left">{formatDateDashed(activity.createdAt)}</p>
                    </div>
                  </div>
                ))}
              </GridTable>
            </div>
          </>
        )}
      </div>
    </>
  )
}

ActivityList.propTypes = {
  onDelete: PropTypes.func,
}

export default ActivityList